import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Check, X, Clock, Image, User, RefreshCw } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

interface AvatarRequest {
  id: string;
  user_id: string;
  file_path: string;
  status: 'pending' | 'approved' | 'rejected';
  admin_note: string | null;
  created_at: string;
  reviewed_at: string | null;
  user_display_name?: string;
  user_email?: string;
}

export function AvatarReviewAdmin() {
  const { session } = useAuth();
  const [avatars, setAvatars] = useState<AvatarRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'pending' | 'approved' | 'rejected' | 'all'>('pending');
  const [selectedAvatar, setSelectedAvatar] = useState<AvatarRequest | null>(null);
  const [rejectionNote, setRejectionNote] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchAvatars();
  }, [filter]);

  const fetchAvatars = async () => {
    setLoading(true);
    try {
      let query = supabase
        .from('profile_avatars')
        .select('*')
        .order('created_at', { ascending: false });

      if (filter !== 'all') {
        query = query.eq('status', filter);
      }

      const { data, error } = await query;

      if (error) throw error;

      // Fetch user display names
      if (data && data.length > 0) {
        const userIds = [...new Set(data.map(a => a.user_id))];
        const { data: profiles } = await supabase
          .from('profiles')
          .select('id, display_name')
          .in('id', userIds);

        const profileMap = new Map(profiles?.map(p => [p.id, p.display_name]) || []);

        const enrichedData = data.map(avatar => ({
          ...avatar,
          user_display_name: profileMap.get(avatar.user_id) || 'Okänd användare',
        }));

        setAvatars(enrichedData as AvatarRequest[]);
      } else {
        setAvatars([]);
      }
    } catch (error: any) {
      console.error('Error fetching avatars:', error);
      toast.error('Kunde inte hämta profilbilder');
    } finally {
      setLoading(false);
    }
  };

  const getAvatarUrl = (filePath: string) => {
    const { data } = supabase.storage.from('avatars').getPublicUrl(filePath);
    return data.publicUrl;
  };

  const handleApprove = async (avatar: AvatarRequest) => {
    setProcessing(true);
    try {
      // Get the public URL for the avatar
      const publicUrl = getAvatarUrl(avatar.file_path);

      // Update avatar request status
      const { error: updateError } = await supabase
        .from('profile_avatars')
        .update({
          status: 'approved',
          reviewed_by: session?.user?.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', avatar.id);

      if (updateError) throw updateError;

      // Update user profile with the approved avatar URL
      const { error: profileError } = await supabase
        .from('profiles')
        .update({
          avatar_url: publicUrl,
          default_avatar: null,
        })
        .eq('id', avatar.user_id);

      if (profileError) throw profileError;

      toast.success('Profilbild godkänd!');
      fetchAvatars();
    } catch (error: any) {
      console.error('Error approving avatar:', error);
      toast.error('Kunde inte godkänna bilden');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!selectedAvatar) return;
    
    setProcessing(true);
    try {
      const { error } = await supabase
        .from('profile_avatars')
        .update({
          status: 'rejected',
          admin_note: rejectionNote || 'Bilden uppfyller inte våra riktlinjer',
          reviewed_by: session?.user?.id,
          reviewed_at: new Date().toISOString(),
        })
        .eq('id', selectedAvatar.id);

      if (error) throw error;

      toast.success('Profilbild nekad');
      setSelectedAvatar(null);
      setRejectionNote('');
      fetchAvatars();
    } catch (error: any) {
      console.error('Error rejecting avatar:', error);
      toast.error('Kunde inte neka bilden');
    } finally {
      setProcessing(false);
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" /> Väntar</Badge>;
      case 'approved':
        return <Badge className="gap-1 bg-green-500"><Check className="h-3 w-3" /> Godkänd</Badge>;
      case 'rejected':
        return <Badge variant="destructive" className="gap-1"><X className="h-3 w-3" /> Nekad</Badge>;
      default:
        return null;
    }
  };

  const pendingCount = avatars.filter(a => a.status === 'pending').length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="font-display flex items-center gap-2">
                <Image className="h-5 w-5" />
                Granska profilbilder
                {pendingCount > 0 && filter === 'pending' && (
                  <Badge variant="destructive">{pendingCount}</Badge>
                )}
              </CardTitle>
              <CardDescription>
                Godkänn eller neka uppladdade profilbilder
              </CardDescription>
            </div>
            <Button variant="outline" size="sm" onClick={fetchAvatars} className="gap-2">
              <RefreshCw className="h-4 w-4" />
              Uppdatera
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filter Buttons */}
          <div className="flex gap-2 mb-6">
            {(['pending', 'approved', 'rejected', 'all'] as const).map((f) => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f)}
              >
                {f === 'pending' && 'Väntande'}
                {f === 'approved' && 'Godkända'}
                {f === 'rejected' && 'Nekade'}
                {f === 'all' && 'Alla'}
              </Button>
            ))}
          </div>

          {loading ? (
            <p className="text-center text-muted-foreground py-8">Laddar...</p>
          ) : avatars.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              {filter === 'pending' 
                ? 'Inga bilder väntar på granskning'
                : 'Inga bilder att visa'
              }
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {avatars.map((avatar) => (
                <Card key={avatar.id} className="overflow-hidden">
                  <div className="aspect-square relative">
                    <img
                      src={getAvatarUrl(avatar.file_path)}
                      alt="Profilbild"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute top-2 right-2">
                      {getStatusBadge(avatar.status)}
                    </div>
                  </div>
                  <CardContent className="p-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <User className="h-4 w-4 text-muted-foreground" />
                      <span className="font-medium">{avatar.user_display_name}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Uppladdad: {new Date(avatar.created_at).toLocaleDateString('sv-SE')}
                    </p>
                    {avatar.admin_note && (
                      <p className="text-xs text-muted-foreground italic">
                        Anmärkning: {avatar.admin_note}
                      </p>
                    )}
                    {avatar.status === 'pending' && (
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          className="flex-1 gap-1"
                          onClick={() => handleApprove(avatar)}
                          disabled={processing}
                        >
                          <Check className="h-4 w-4" />
                          Godkänn
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="flex-1 gap-1"
                          onClick={() => {
                            setSelectedAvatar(avatar);
                            setRejectionNote('');
                          }}
                          disabled={processing}
                        >
                          <X className="h-4 w-4" />
                          Neka
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Rejection Dialog */}
      <Dialog open={!!selectedAvatar} onOpenChange={(open) => !open && setSelectedAvatar(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neka profilbild</DialogTitle>
            <DialogDescription>
              Ange en anledning som visas för användaren.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="rejection-note">Anledning</Label>
              <Textarea
                id="rejection-note"
                value={rejectionNote}
                onChange={(e) => setRejectionNote(e.target.value)}
                placeholder="T.ex. 'Bilden innehåller olämpligt innehåll'"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedAvatar(null)}>
              Avbryt
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleReject}
              disabled={processing}
            >
              {processing ? 'Nekar...' : 'Neka bild'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
